import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../services/api';
import { useWallet } from '../context/WalletContext';
import PageHeader from '../components/PageHeader';
import { rankTitle } from '../utils/ranks';

function pct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n}%`;
}

function usdt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function unlockStatus(state, row) {
  if (state?.met) return 'Open';
  const first = `${state?.gen1 ?? 0}/${row.gen1_count}`;
  if (Number(row.gen2_count) > 0) return `${first} · ${state?.gen2 ?? 0}/${row.gen2_count}`;
  return first;
}

export default function CommissionPage() {
  const { user, loading: walletLoading } = useWallet();
  const [charts, setCharts] = useState(null);
  const [unlock, setUnlock] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (walletLoading) return undefined;
    let cancelled = false;
    setLoading(true);
    const request = user ? api.myCommissions() : api.commissions();
    request
      .then((data) => {
        if (cancelled) return;
        setCharts(data.charts || null);
        setUnlock(data.unlock || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load commissions');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.wallet_address, walletLoading]);

  const roi = charts?.roi_downline;
  const levels = roi?.levels || [];
  const minRank = roi?.extra_min_rank ?? 7;
  const progress = new Map((unlock?.levels || []).map((row) => [row.level, row]));

  return (
    <>
      <PageHeader title="Commission" subtitle="Rates paid from downline activity" />
      <div className="rank-page">
        <motion.section className="panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h2>ROI downline</h2>
          <p className="muted">
            Level 1 is open. Higher levels need the user counts below.
            {user ? ` You are unlocked through level ${unlock?.level ?? 0}.` : ''}
            {' '}Commission is paid only after you invest at least {usdt(roi?.min_self_invest ?? 100)} USDT.
            {rankTitle(minRank)}+ adds {pct(roi?.extra_percent)}.
          </p>
          {error ? <p className="muted">{error}</p> : null}
          {loading ? <p className="muted">Loading…</p> : null}
          {!loading && !error ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th>Base</th>
                    <th>{rankTitle(minRank)}+</th>
                    <th>1st level</th>
                    <th>Min</th>
                    <th>2nd level</th>
                    <th>Min</th>
                    {user ? <th>Status</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {levels.map((row) => {
                    const state = progress.get(row.level);
                    return (
                      <tr key={row.level} className={state?.met ? 'is-current' : undefined}>
                        <td>Level {row.level}</td>
                        <td>{pct(row.percent)}</td>
                        <td>{pct(row.boosted)}</td>
                        <td>{row.gen1_count}</td>
                        <td>{usdt(row.gen1_min)}</td>
                        <td>{row.gen2_count}</td>
                        <td>{usdt(row.gen2_min)}</td>
                        {user ? (
                          <td>{unlockStatus(state, row)}</td>
                        ) : null}
                      </tr>
                    );
                  })}
                  {!levels.length ? (
                    <tr>
                      <td colSpan={user ? 8 : 7} className="muted">No ROI rates set.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}
        </motion.section>
      </div>
    </>
  );
}
